import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  MapPin,
  Star,
  Heart,
  Clock,
  Check,
  Coffee,
  UtensilsCrossed,
  BedDouble,
  Circle, 
  Code,
  Calendar,
  Users,
  Edit2,
  ChevronDown,
} from "lucide-react";
import "./CSS/Hotel.css";

const HOTEL_SEARCH_API = "https://api.hotel.namantechnolab.com/api/hotel/search";

function currencySymbolFor(code) {
  if (code === "INR") return "₹";
  if (code === "USD") return "$";
  if (code === "GBP") return "£";
  if (code === "EUR") return "€";
  return code || "₹";
}

function getBestRoom(hotel) {
  const rooms = Array.isArray(hotel.TripRooms) ? hotel.TripRooms : [];
  if (rooms.length === 0) return null;

  const match = rooms.find(
    (r) => Number(r.Totalprice) === Number(hotel.BestPrice)
  );
  if (match) return match;

  return rooms.reduce((cheapest, r) =>
    Number(r.Totalprice) < Number(cheapest.Totalprice) ? r : cheapest
  , rooms[0]);
}

function formatSummaryDate(isoDate) {
  if (!isoDate) return "--";
  const d = new Date(isoDate);
  if (isNaN(d)) return isoDate;
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function mealPerk(mealbasis) {
  if (!mealbasis) return null;
  const lower = mealbasis.toLowerCase();
  if (lower.includes("breakfast")) {
    return { icon: Coffee, text: "Breakfast Included" };
  }
  if (lower.includes("half board")) {
    return { icon: UtensilsCrossed, text: "Half Board — Breakfast & 1 meal included" };
  }
  if (lower.includes("full board")) {
    return { icon: UtensilsCrossed, text: "Full Board — All meals included" };
  }
  return null;
}

function HotelCard({ hotel, onSelect }) {
  const name = hotel.HotelName || "Unnamed Hotel";
  const image = hotel.HotelImage || "";
  const addressLine = [hotel.HotelAddress, hotel.City]
    .filter(Boolean)
    .join(", ");
  const stars = Number(hotel.Rating) || 0;

  const price = hotel.BestPrice;
  const currencySymbol = currencySymbolFor(hotel.Selectcurrency);

  const roomCount = Array.isArray(hotel.TripRooms) ? hotel.TripRooms.length : 0;
  const bestRoom = getBestRoom(hotel);
  const perk = bestRoom ? mealPerk(bestRoom.Mealbasis) : null;

  return (
    <div className="hotel-card">
      <div className="hotel-card__main">
        <div className="hotel-card__media">
          {image ? (
            <img
              src={image}
              alt={name}
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <div className="hotel-card__media-placeholder" />
          )}
          <button type="button" className="hotel-card__wishlist" aria-label="Save to wishlist">
            <Heart size={16} />
          </button>
          {roomCount > 0 && (
            <div className="hotel-card__photo-count">
              <BedDouble size={13} />
              <span>{roomCount} room option{roomCount > 1 ? "s" : ""} available</span>
            </div>
          )}
        </div>

        <div className="hotel-card__details">
          <div className="hotel-card__title-row">
            <span className="hotel-card__verified" aria-hidden="true">
              <Check size={12} />
            </span>
            <h4
              className="hotel-card__name"
              onClick={() => onSelect(hotel)}
              style={{ cursor: "pointer" }}
            >
              {name}
            </h4>
            {stars > 0 && (
              <span className="hotel-card__stars">
                {Array.from({ length: stars }, (_, i) => (
                  <Star key={i} size={13} fill="currentColor" />
                ))}
              </span>
            )}
          </div>

          {addressLine && (
            <div className="hotel-card__locality">
              <MapPin size={12} /> {addressLine}
            </div>
          )}

          <ul className="hotel-card__perks">
            <li>
              <Clock size={14} />
              <span>Guaranteed Early Check-in/Late Check-out available</span>
            </li>
            <li>
              <Check size={14} />
              <span>Free Cancellation till 24 hrs before check in</span>
            </li>
            <li>
              <Check size={14} />
              <span>Book with ₹0 Payment</span>
            </li>
            {perk && (
              <li>
                <perk.icon size={14} />
                <span>{perk.text}</span>
              </li>
            )}
            {bestRoom?.RoomType && (
              <li>
                <BedDouble size={14} />
                <span>{bestRoom.RoomType}</span>
              </li>
            )}
          </ul>
        </div>

        <div className="hotel-card__price-panel">
          {price !== undefined && price !== null && (
            <div className="hotel-card__price">
              <span className="hotel-card__price-amount">
                {currencySymbol} {Number(price).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
              </span>
              <span className="hotel-card__price-per">
                Starting price {roomCount > 1 ? `(${roomCount} options)` : ""}
              </span>
            </div>
          )}

          <a className="hotel-card__login-link" href="#login">
            Login to Book Now &amp; Pay Later!
          </a>
        </div>
      </div>
    </div>
  );
}

export default function HotelResults() {
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let stored = null;
    let storedResponse = null;
    try {
      stored = JSON.parse(sessionStorage.getItem("hotelSearchPayload") || "null");
      storedResponse = JSON.parse(sessionStorage.getItem("hotelSearchResponse") || "null");
    } catch (err) {
      console.error("Could not read hotel search data", err);
    }

    if (!stored) {
      setLoading(false);
      setError("No search payload found. Please search again from the home page.");
      return;
    }

    setPayload(stored);

    if (storedResponse) {
      console.log("Hotel search response:", storedResponse);
      setResponse(storedResponse);
      setLoading(false);
      return;
    }

    runSearch(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (searchPayload) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(HOTEL_SEARCH_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(searchPayload),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const data = await res.json();
      console.log("Hotel search response:", data);
      setResponse(data);

      try {
        sessionStorage.setItem("hotelSearchResponse", JSON.stringify(data));
      } catch (err) {
        console.error("Could not persist hotel search response", err);
      }
    } catch (err) {
      console.error("Hotel search request failed:", err);
      setError(err.message || "Something went wrong while fetching hotels.");
    } finally {
      setLoading(false);
    }
  };

  // Hotel name click -> store selected hotel -> navigate to details page with TripId
  const handleHotelClick = (hotel) => {
    const tripId = hotel?.TripId;
    if (!tripId) {
      console.error("TripId missing on selected hotel", hotel);
      return;
    }

    try {
      sessionStorage.setItem("selectedHotel", JSON.stringify(hotel));
      // purani detail response clear kar do taaki naye hotel ka fresh data aaye
      sessionStorage.removeItem("hotelDetailsResponse");
    } catch (err) {
      console.error("Could not store selected hotel", err);
    }

    navigate(`/hotel-details/${tripId}`);
  };

  const hotelList = Array.isArray(response?.TripHotels) ? response.TripHotels : [];

  return (
    <section className="hotel-results">
      <div className="hotel-results__container">

        {payload && (
          <div className="hotel-results__summary">
            <div className="hotel-results__summary-item">
              <span className="hotel-results__summary-label">
                <MapPin size={13} /> Place
              </span>
              <span className="hotel-results__summary-value">{payload.City}</span>
            </div>

            <div className="hotel-results__summary-divider" />

            <div className="hotel-results__summary-item">
              <span className="hotel-results__summary-label">
                <Calendar size={13} /> Check In
              </span>
              <span className="hotel-results__summary-value">
                {formatSummaryDate(payload.CheckIn)}
              </span>
            </div>

            <div className="hotel-results__summary-divider" />

            <div className="hotel-results__summary-item">
              <span className="hotel-results__summary-label">
                <Calendar size={13} /> Check Out
              </span>
              <span className="hotel-results__summary-value">
                {formatSummaryDate(payload.CheckOut)}
              </span>
            </div>

            <div className="hotel-results__summary-divider" />

            <div className="hotel-results__summary-item">
              <span className="hotel-results__summary-label">
                <Users size={13} /> Rooms &amp; Guests
              </span>
              <span className="hotel-results__summary-value">
                Room: {payload.RoomCount}, Adults: {payload.Numofadults}, Children:{" "}
                {payload.NumofChild}
              </span>
            </div>

            <button
              type="button"
              className="hotel-results__modify-btn"
              onClick={() => navigate("/hotel")}
            >
              <Edit2 size={14} /> Modify <ChevronDown size={14} />
            </button>
          </div>
        )}

        {loading && (
          <div className="hotel-results__status">Searching for hotels...</div>
        )}

        {!loading && error && (
          <div className="hotel-results__status hotel-results__status--error">
            {error}
          </div>
        )}

        {!loading && !error && response && (
          <div className="hotel-results__list">
            {hotelList.length > 0 ? (
              <div className="hotel-results__stack">
                {hotelList.map((hotel) => (
                  <HotelCard
                    key={hotel.TripId || hotel.uid}
                    hotel={hotel}
                    onSelect={handleHotelClick}
                  />
                ))}
              </div>
            ) : (
              <div className="hotel-results__status">
                No hotels found for your search.
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}